<template>
  <div>
    <fieldset>
      <legend class="sr-only">What is the max distance you'll travel for a job?</legend>
      <Dropdown
        id="max-distance"
        ref="maxDistanceDropdown"
        v-model="distanceWillingToTravel"
        labelName="# of miles"
        :options="options"
        :status="status"
        :helperText="helperText"
        :required="true"
      />
    </fieldset>

    <div v-if="showMap" class="active-jobs-map mt-8">
      <gmap-map
        :center="{ lat: geoPoint.y, lng: geoPoint.x }"
        :zoom="map.zoom"
        :map-type-id="map.mapTypeId"
        :options="map.mapOptions"
      >
        <gmap-circle
          :center="{ lat: geoPoint.y, lng: geoPoint.x }"
          :radius="map.radius"
          :draggable="map.circleDraggable"
          :editable="map.circleEditable"
          :options="map.circleOptions"
        />
      </gmap-map>
      <Card customClasses="jobs-card max-w-232 mx-auto w-full" :elevation="3">
        <div v-if="jobsCountLoading" class="flex gap-8 m-16">
          <Spinner />
          <p class="text-body">Calculating active jobs near you</p>
        </div>
        <div v-else class="flex gap-8">
          <PlaceIcon :width="32" :height="32" color="#FF0064" class="mt-4" aria-hidden="true" />
          <div>
            <p class="text-h2" aria-hidden="true">{{ totalJobsCount }}</p>
            <p class="text-body" aria-hidden="true">active jobs near you</p>
            <span class="sr-only">{{ totalJobsCount }} active jobs near you</span>
            <p class="text-overline text-primary">{{ newJobsCount }} new today</p>
          </div>
        </div>
      </Card>
    </div>
  </div>
</template>

<script lang="ts">
import { mapState } from 'vuex';
import Dropdown from '@/components/dropdown/Dropdown.vue';
import Card from '@/components/card/Card.vue';
import Spinner from '@/components/spinner/Spinner.vue';
import PlaceIcon from '@/graphics/icons/PlaceIcon.vue';
import helperStatus from '@/helpers/status';
import OnboardingQueries from '@/graphql/onboarding/queries';
import focusOnElement from '@/helpers/focusHelper';

export default {
  name: 'DistanceStep',
  components: { Dropdown, Card, Spinner, PlaceIcon },
  data(): any {
    return {
      status: helperStatus.Neutral,
      options: [
        { value: 1, display: '1 mile' },
        { value: 2, display: '2 miles' },
        { value: 5, display: '5 miles' },
        { value: 10, display: '10 miles' },
        { value: 15, display: '15 miles' },
        { value: 25, display: '25 miles' },
        { value: 40, display: '40 miles' },
        { value: 50, display: '50 miles' },
      ],
      map: {
        mapTypeId: 'roadmap',
        zoom: 9,
        radius: 24140.2,
        mapOptions: {
          disableDefaultUI: true,
          draggable: false,
          keyboardShortcuts: false,
        },
        circleDraggable: false,
        circleEditable: false,
        circleOptions: {
          fillColor: '#bb0049',
          fillOpacity: 0.2,
          strokeColor: 'transparent',
          strokeWeight: 0,
        },
      },
      geoPoint: {
        x: null,
        y: null,
      },
      mapLoading: true,
      jobsCountLoading: true,
      totalJobsCount: 0,
      newJobsCount: 0,
      fetchError: false,
    };
  },
  computed: {
    ...mapState({ careType: (state) => state.sitterOnboarding.careType }),
    distanceWillingToTravel: {
      get() {
        return this.$store.getters.getFormField('distanceWillingToTravel');
      },
      set(newValue) {
        this.status = helperStatus.Neutral;
        this.$store.dispatch('updateForm', { key: 'distanceWillingToTravel', value: newValue });
      },
    },
    showMap(): boolean {
      return !this.mapLoading && !this.fetchError && this.totalJobsCount > 0;
    },
    helperText(): String {
      return this.status === helperStatus.Error ? "Select the maximum distance you'll travel" : '';
    },
  },
  mounted() {
    this.fetchSitterGeopoint();
  },
  methods: {
    fetchSitterGeopoint() {
      this.$apollo
        .query({
          query: OnboardingQueries.FETCH_SITTER_LOCATION,
        })
        .then(({ data }) => {
          this.geoPoint = {
            x: parseFloat(data?.viewer?.location?.geoPoint?.x),
            y: parseFloat(data?.viewer?.location?.geoPoint?.y),
          };
          this.fetchJobCount();
          this.mapLoading = false;
        })
        .catch((error) => {
          this.fetchError = true;
          console.log(error.message); // eslint-disable-line no-console
        });
    },
    fetchJobCount() {
      const searchParams = {
        careType: this.careType.toUpperCase(),
        distance: 15,
        geoPoint: this.geoPoint,
        scheduleTypes: ['REGULARLY_SCHEDULED', 'ONE_TIME', 'OCCASIONAL'],
      };
      const postedAfter = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();

      this.jobsCountLoading = true;
      this.$apollo
        .query({
          // Fetch total jobs within distance
          query: OnboardingQueries.FETCH_JOB_COUNT,
          variables: { searchParams },
        })
        .then(({ data }) => {
          this.totalJobsCount = data.jobSearch.totalCount;

          return this.$apollo.query({
            // Fetch total jobs within distance posted in the last 24 hours
            query: OnboardingQueries.FETCH_JOB_COUNT,
            variables: { searchParams: { ...searchParams, postedAfter } },
          });
        })
        .then(({ data }) => {
          this.newJobsCount = data.jobSearch.totalCount;
          this.jobsCountLoading = false;
        })
        .catch((error) => {
          this.fetchError = true;
          console.log(error.message); // eslint-disable-line no-console
        });
    },
    focusElement() {
      focusOnElement(this.$refs.maxDistanceDropdown.$el.querySelector('select'));
    },
    validate() {
      const isValid = !!this.distanceWillingToTravel;
      this.status = isValid ? helperStatus.Neutral : helperStatus.Error;
      return isValid;
    },
  },
};
</script>

<style lang="scss" scoped>
.jobs-card {
  top: 65%;
  left: 50%;
  transform: translate(-50%, -50%);
  position: absolute !important;
  border-radius: 25px !important;

  @media (min-width: 480px) {
    top: 50%;
  }
}
</style>
